/**
 * MapTools - Circle & Line Measurement Tools
 *
 * Provides:
 * - Circle measurement tool with radius/area tooltip
 * - Line distance measurement tool
 *
 * Adapted from LUPMIS2 PWA MapTools for BisaWofa.
 */

import { Draw } from 'ol/interaction';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import Overlay from 'ol/Overlay';
import { LineString, Circle } from 'ol/geom';
import { getLength } from 'ol/sphere';
import Feature from 'ol/Feature';
import { unByKey } from 'ol/Observable';

export class MapTools {
  constructor(map, options = {}) {
    this.map = map;
    this.options = options;

    // Create measurement layer
    this.measureSource = new VectorSource();
    this.measureLayer = new VectorLayer({
      source: this.measureSource,
      style: this.getMeasureStyle(),
      title: 'Measurements',
      zIndex: 100,
    });

    // Insert measurement layer just before the last layer (Overlays group)
    const layers = this.map.getLayers();
    const overlayIdx = layers.getLength() - 1;
    layers.insertAt(overlayIdx, this.measureLayer);

    // Active interaction
    this.activeInteraction = null;
    this.measureTooltip = null;
    this.measureTooltipElement = null;

    // Callbacks
    this.onMeasureCompleteCallbacks = [];
  }

  /**
   * Get style for measurement features
   */
  getMeasureStyle() {
    return new Style({
      fill: new Fill({
        color: 'rgba(255, 233, 106, 0.2)',
      }),
      stroke: new Stroke({
        color: '#8B008B',
        lineDash: [10, 10],
        width: 2,
      }),
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
          color: '#8B008B',
        }),
        fill: new Fill({
          color: 'rgba(255, 233, 106, 0.5)',
        }),
      }),
    });
  }

  /**
   * Format length output
   */
  formatLength(length) {
    if (length > 1000) {
      return (Math.round((length / 1000) * 100) / 100) + ' km';
    } else {
      return (Math.round(length * 100) / 100) + ' m';
    }
  }

  /**
   * Format area output
   */
  formatArea(area) {
    if (area > 1000000) {
      return (Math.round((area / 1000000) * 100) / 100) + ' km²';
    } else {
      return (Math.round(area * 100) / 100) + ' m²';
    }
  }

  /**
   * Format circle extent (area from radius)
   */
  formatCircleExtent(radius) {
    const area = Math.PI * radius * radius;
    if (area > 1000000) {
      return (Math.round((area / 1000000) * 100) / 100) + ' km²';
    } else {
      return (Math.round(area * 100) / 100) + ' m²';
    }
  }

  /**
   * Create measurement tooltip overlay
   */
  createMeasureTooltip() {
    if (this.measureTooltipElement) {
      this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement);
    }
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'measure-tooltip';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [15, 0],
      positioning: 'center-left',
      stopEvent: false,
    });
    this.map.addOverlay(this.measureTooltip);
  }

  /**
   * Remove any active interaction
   */
  deactivate() {
    if (this.activeInteraction) {
      this.map.removeInteraction(this.activeInteraction);
      this.activeInteraction = null;
    }
    if (this.measureTooltip) {
      this.map.removeOverlay(this.measureTooltip);
      this.measureTooltip = null;
    }
    if (this.measureTooltipElement && this.measureTooltipElement.parentNode) {
      this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement);
      this.measureTooltipElement = null;
    }
  }

  /**
   * Start circle measurement tool
   * Draws a circle and shows radius + area in tooltip
   */
  startCircleMeasure() {
    this.deactivate();
    this.createMeasureTooltip();

    const drawCircle = new Draw({
      source: this.measureSource,
      type: 'Circle',
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 233, 106, 0.2)',
        }),
        stroke: new Stroke({
          color: 'rgba(139, 0, 139, 0.7)',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(139, 0, 139, 0.7)',
          }),
          fill: new Fill({
            color: 'rgba(255, 233, 106, 0.5)',
          }),
        }),
      }),
    });

    this.activeInteraction = drawCircle;
    this.map.addInteraction(drawCircle);

    let listener;

    drawCircle.on('drawstart', (evt) => {
      const sketch = evt.feature;

      listener = sketch.getGeometry().on('change', (e) => {
        const geom = e.target;

        if (geom instanceof Circle) {
          const radius = geom.getRadius();
          const area = this.formatCircleExtent(radius);
          const radiusFormatted = this.formatLength(radius);

          const output = `<strong>${radiusFormatted}</strong><br><small>${area}</small>`;

          this.measureTooltipElement.innerHTML = output;
          this.measureTooltip.setPosition(geom.getLastCoordinate());
        }
      });
    });

    drawCircle.on('drawend', (evt) => {
      const feature = evt.feature;
      const geom = feature.getGeometry();
      const center = geom.getCenter();
      const radius = geom.getRadius();

      // Tag the circle feature for identification
      feature.set('_layerType', 'measure_circle');
      feature.set('_radius', radius);
      feature.set('_center', center);

      // Create radius line for visualization
      const radiusLine = new Feature({
        geometry: new LineString([
          center,
          [center[0] + radius, center[1]],
        ]),
      });
      radiusLine.set('_layerType', 'measure_circle_radius');
      this.measureSource.addFeature(radiusLine);

      // Make tooltip static
      this.measureTooltipElement.className = 'measure-tooltip measure-tooltip-static';
      this.measureTooltip.setOffset([0, -7]);

      // Create new tooltip for next measurement
      this.measureTooltipElement = null;
      this.createMeasureTooltip();

      unByKey(listener);

      // Trigger callbacks
      const result = {
        type: 'circle',
        center: center,
        radius: radius,
        area: Math.PI * radius * radius,
        feature: feature,
      };
      this.onMeasureCompleteCallbacks.forEach((cb) => cb(result));
    });

    return drawCircle;
  }

  /**
   * Start line measurement tool
   */
  startLineMeasure() {
    this.deactivate();
    this.createMeasureTooltip();

    const drawLine = new Draw({
      source: this.measureSource,
      type: 'LineString',
      style: this.getMeasureStyle(),
    });

    this.activeInteraction = drawLine;
    this.map.addInteraction(drawLine);

    let listener;

    drawLine.on('drawstart', (evt) => {
      const sketch = evt.feature;

      listener = sketch.getGeometry().on('change', (e) => {
        const geom = e.target;
        const length = getLength(geom);
        const output = this.formatLength(length);

        this.measureTooltipElement.innerHTML = output;
        this.measureTooltip.setPosition(geom.getLastCoordinate());
      });
    });

    drawLine.on('drawend', (evt) => {
      const feature = evt.feature;
      const geom = feature.getGeometry();
      const length = getLength(geom);

      this.measureTooltipElement.className = 'measure-tooltip measure-tooltip-static';
      this.measureTooltipElement = null;
      this.createMeasureTooltip();

      unByKey(listener);

      const result = {
        type: 'line',
        length: length,
        feature: feature,
      };
      this.onMeasureCompleteCallbacks.forEach((cb) => cb(result));
    });

    return drawLine;
  }

  /**
   * Clear all measurements
   */
  clearMeasurements() {
    this.measureSource.clear();
    // Remove static tooltips
    const tooltips = document.querySelectorAll('.measure-tooltip-static');
    tooltips.forEach((el) => el.parentNode.removeChild(el));
  }

  /**
   * Register callback for measurement completion
   */
  onMeasureComplete(callback) {
    this.onMeasureCompleteCallbacks.push(callback);
  }

  /**
   * Get the measure layer
   */
  getMeasureLayer() {
    return this.measureLayer;
  }

  /**
   * Get the measure source
   */
  getMeasureSource() {
    return this.measureSource;
  }

  /**
   * Check if any tool is currently active
   */
  isActive() {
    return this.activeInteraction !== null;
  }
}

export default MapTools;
